import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';

import { VideoTemplatePreviewCard } from './VideoTemplatePreviewCard';

import type { VideoTemplateConfig, VideoTemplateConfigured } from './types';

interface VideoTemplatesGridProps {
  videoTemplates: VideoTemplateConfigured[];
  onAddVideoTemplate: (videoTemplate: VideoTemplateConfigured) => void;
  onSettingsClick?: () => void;
  onClearClick?: () => void;
  activeTemplateId?: string | null;
  disabled?: boolean;
  onCreateNew?: () => void;
  /** Keeps the create card in a pending state while the template is created. */
  isCreating?: boolean;
  actionLabel?: string;
}

/** Extract human-readable composition labels from a template config. */
function getCompositionLabels(config: VideoTemplateConfig | null): string[] {
  if (!config) return [];
  const labels: string[] = [];
  if (config.logoConfiguration?.enabled) labels.push('Logo');
  if (config.introCardConfiguration?.enabled) labels.push('Intro Card');
  if (config.endCardConfiguration?.enabled) labels.push('End Card');
  if (config.captionsConfiguration?.enabled) labels.push('Captions');
  if (config.namecardConfiguration?.enabled) labels.push('Namecard');
  return labels;
}

/** Format composition labels as a comma-separated list. */
function formatCompositionLabels(labels: string[]): string {
  return labels.join(', ');
}

/** Extract caption-related colors (hex) from a template config. */

export const VideoTemplatesGrid = observer((props: VideoTemplatesGridProps) => {
  const {
    videoTemplates,
    onAddVideoTemplate,
    onSettingsClick,
    onClearClick,
    activeTemplateId,
    disabled,
    onCreateNew,
    isCreating,
    actionLabel = 'Apply',
  } = props;

  const renderVideoTemplateCard = useCallback(
    (videoTemplate: VideoTemplateConfigured) => {
      const compositionLabels = getCompositionLabels(
        videoTemplate.configuration,
      );

      const isActive = activeTemplateId === videoTemplate.id;

      return (
        <TemplateCard
          key={videoTemplate.id}
          videoTemplate={videoTemplate}
          isActive={isActive}
          disabled={disabled}
          compositionLabels={compositionLabels}
          onAddVideoTemplate={onAddVideoTemplate}
          onSettingsClick={onSettingsClick}
          onClearClick={onClearClick}
          actionLabel={actionLabel}
        />
      );
    },
    [
      activeTemplateId,
      onAddVideoTemplate,
      onSettingsClick,
      disabled,
      actionLabel,
    ],
  );

  return (
    <>
      <GridWrapper>
        {onCreateNew && (
          <CreateNewCard
            onClick={disabled || isCreating ? undefined : onCreateNew}
            disabled={disabled}
            $isCreating={isCreating}
          >
            <PlusIcon>+</PlusIcon>
            {!disabled && (
              <CreateNewOverlay>
                <PresetDimOverlay />
                <GlassButtonWrapper>
                  <GlassButton>
                    {isCreating ? 'Creating...' : 'Create Template'}
                  </GlassButton>
                </GlassButtonWrapper>
              </CreateNewOverlay>
            )}
          </CreateNewCard>
        )}
        {videoTemplates.map(renderVideoTemplateCard)}
      </GridWrapper>
    </>
  );
});

/** Individual template card with Apply/Settings hover behavior. */
function TemplateCard({
  videoTemplate,
  isActive,
  disabled,
  compositionLabels,
  onAddVideoTemplate,
  onSettingsClick,
  onClearClick,
  actionLabel = 'Apply',
}: {
  videoTemplate: VideoTemplateConfigured;
  isActive: boolean;
  disabled?: boolean;
  compositionLabels: string[];
  onAddVideoTemplate: (vt: VideoTemplateConfigured) => void;
  onSettingsClick?: () => void;
  onClearClick?: () => void;
  actionLabel?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (isActive && onSettingsClick) {
      onSettingsClick();
    } else {
      onAddVideoTemplate(videoTemplate);
    }
  };

  return (
    <CardContainer
      disabled={disabled}
      data-testid={`video-template-card-${videoTemplate.id}`}
    >
      <CardBorder
        isActive={isActive}
        isHovered={isHovered}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={disabled ? undefined : handleClick}
      >
        <CardInner>
          <CardHeader>
            <CardTitle>{videoTemplate.label}</CardTitle>
          </CardHeader>
          <HoverablePreview>
            <VideoTemplatePreviewCard videoTemplate={videoTemplate} />
            {isHovered && !disabled && (
              <>
                <PresetDimOverlay />
                <GlassButtonWrapper>
                  {isActive && onSettingsClick ? (
                    <>
                      <GlassButton>Settings</GlassButton>
                      {onClearClick && (
                        <GlassButton
                          onClick={e => {
                            e.stopPropagation();
                            onClearClick();
                          }}
                        >
                          Clear
                        </GlassButton>
                      )}
                    </>
                  ) : (
                    <GlassButton>{actionLabel}</GlassButton>
                  )}
                </GlassButtonWrapper>
              </>
            )}
          </HoverablePreview>
          <CardFooter>
            <CompositionLabels>
              {compositionLabels.length > 0
                ? formatCompositionLabels(compositionLabels)
                : 'No Graphics'}
            </CompositionLabels>
          </CardFooter>
        </CardInner>
      </CardBorder>
    </CardContainer>
  );
}

const GridWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  max-width: 100%;
  overflow: hidden;
`;

const HoverablePreview = styled.div`
  cursor: pointer;
  transition: var(--transition-normal);
  padding: 0 10px;
  position: relative;
`;

// const CompositionCardWrapper = styled.div<{ disabled?: boolean }>`
const CardContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    `}
`;

const CardInner = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border-radius: 6px;
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 10px 10px 8px;
`;

const CardTitle = styled.div`
  color: #fff;
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 10px 10px;
  gap: 5px;
`;

const CompositionLabels = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CreateNewOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.15s ease;
  border-radius: 8px;
  overflow: hidden;
`;

const CreateNewCard = styled.div<{
  disabled?: boolean;
  $isCreating?: boolean;
}>`
  width: 100%;
  min-height: 100%;
  border-radius: 8px;
  border: 2px dashed var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
  position: relative;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    `}

  &:hover {
    border-color: var(--primary);
  }

  &:hover ${CreateNewOverlay} {
    opacity: 1;
  }

  &:hover > span {
    opacity: 0;
  }

  /* While creating, hold the pending state visible without needing hover. */
  ${props =>
    props.$isCreating &&
    css`
      cursor: default;

      ${CreateNewOverlay} {
        opacity: 1;
      }

      > span {
        opacity: 0;
      }
    `}
`;

const PlusIcon = styled.span`
  color: var(--text-muted);
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
  transition: opacity 0.15s ease;
`;

const CardBorder = styled.div<{ isActive: boolean; isHovered: boolean }>`
  border-radius: 8px;
  border: 2px solid
    ${props =>
      props.isActive || props.isHovered ? 'var(--primary)' : 'transparent'};
  transition: border-color 0.15s ease;
  overflow: hidden;
  cursor: pointer;
`;

const PresetDimOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.56);
  border-radius: var(--radius-input);
  z-index: 2;
  pointer-events: none;
`;

const GlassButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  gap: 6px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
`;

const GlassButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  white-space: nowrap;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;
